import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { CloudApiService, Package } from '@/cloud/services/cloud-api.service';
import { TranslationService } from '@/layout/service/translation.service';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { GitHubErrorCode, GitHubIssue, GitHubIssueState, apiError, errorDetail, githubErrorCode, githubRepositoryUrl, isRoomLabel, issueRoute, labelTextColor } from '@/cloud/models/github.models';

const PER_PAGE = 30;

/**
 * Slim GitHub issues list for a package: the issues of the package's linked repository (open by default,
 * with closed/all) and paging, each row opening the issue in the Issues section where it is managed.
 * Explains, per API error code, what is missing (no repository on the package, no tenant token, a
 * rejected token, a rate limit).
 */
@Component({
    selector: 'app-package-issues',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, ButtonModule, MessageModule, SelectButtonModule, TagModule, TooltipModule, EmptyStateComponent, LoadingSkeletonComponent],
    templateUrl: './package-issues.html',
    styleUrl: './package-issues.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackageIssues {
    private api = inject(CloudApiService);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;

    /** The package whose repository's issues are shown. */
    pkg = input.required<Package>();
    /** The user may connect GitHub for the tenant (namespace.manage); shows the link to tenant settings. */
    canConfigureTenant = input(false);

    state = signal<GitHubIssueState>('open');
    page = signal(1);
    issues = signal<GitHubIssue[]>([]);
    hasMore = signal(false);
    loading = signal(false);
    errorCode = signal<GitHubErrorCode | string | null>(null);
    errorText = signal<string | null>(null);
    retryAtUtc = signal<string | null>(null);

    readonly repository = computed(() => this.pkg().gitHubRepository ?? null);
    readonly repositoryUrl = computed(() => (this.repository() ? githubRepositoryUrl(this.repository()!) : null));
    /** The Issues section filtered to this repository. */
    readonly issuesLink = computed(() => ({ path: ['/issues'], query: this.repository() ? { repository: this.repository() } : {} }));
    readonly stateOptions = computed(() => [
        { label: this.t()('github.state.open'), value: 'open' as GitHubIssueState },
        { label: this.t()('github.state.closed'), value: 'closed' as GitHubIssueState },
        { label: this.t()('github.state.all'), value: 'all' as GitHubIssueState }
    ]);
    readonly tenantSettingsLink = computed(() => ['/admin/tenants', this.pkg().appNamespaceId, 'integrations']);
    /** Message for the current error code, or null when the error is generic (shown as text). */
    readonly errorMessage = computed(() => {
        const code = this.errorCode();
        const t = this.t();
        switch (code) {
            case null:
                return null;
            case 'package_repository_missing':
                return t('github.repositoryMissing');
            case 'github_not_configured':
                return t('github.notConfigured');
            case 'github_token_rejected':
                return t('github.tokenRejected');
            case 'github_repository_not_found':
                return t('github.repositoryNotFound').replace('{repo}', this.repository() ?? '');
            case 'github_rate_limited': {
                const at = this.retryAtUtc();
                return t('github.rateLimited').replace('{time}', at ? `(${new Date(at).toLocaleTimeString()})` : '');
            }
            case 'github_unavailable':
                return t('github.unavailable');
            case 'github_forbidden':
                return t('github.forbidden');
            case 'encryption_not_configured':
            case 'encryption_key_mismatch':
                return t('github.encryptionMissing');
            default:
                return this.errorText() ?? t('github.loadFailed');
        }
    });
    readonly errorSeverity = computed(() => (this.errorCode() === 'package_repository_missing' || this.errorCode() === 'github_not_configured' ? 'info' : 'warn'));
    readonly showTenantLink = computed(() => this.canConfigureTenant() && (this.errorCode() === 'github_not_configured' || this.errorCode() === 'github_token_rejected'));

    private request = 0;

    constructor() {
        effect(() => {
            const id = this.pkg().id;
            // A repository change on the package should reload too.
            this.repository();
            const state = this.state();
            const page = this.page();
            untracked(() => void this.load(id, state, page));
        });
        this.destroyRef.onDestroy(() => ++this.request);
    }

    labelStyle(color: string | null): Record<string, string> {
        return color ? { background: `#${color}`, color: labelTextColor(color) } : {};
    }

    issueLink(issue: GitHubIssue): string[] {
        return issueRoute(this.repository() ?? '', issue.number);
    }

    visibleLabels(issue: GitHubIssue) {
        return issue.labels.filter((l) => !isRoomLabel(l.name));
    }

    setState(state: GitHubIssueState): void {
        if (state === this.state()) return;
        this.page.set(1);
        this.state.set(state);
    }

    nextPage(): void {
        if (this.hasMore()) this.page.update((p) => p + 1);
    }

    prevPage(): void {
        if (this.page() > 1) this.page.update((p) => p - 1);
    }

    retry(): void {
        void this.load(this.pkg().id, this.state(), this.page());
    }

    async load(packageId: string, state: GitHubIssueState, page: number): Promise<void> {
        const request = ++this.request;
        if (!this.repository()) {
            this.issues.set([]);
            this.hasMore.set(false);
            this.errorCode.set('package_repository_missing');
            this.loading.set(false);
            return;
        }
        this.loading.set(true);
        this.errorCode.set(null);
        this.errorText.set(null);
        this.retryAtUtc.set(null);
        try {
            const result = await this.api.listPackageIssues(packageId, state, page, PER_PAGE);
            if (request !== this.request) return;
            this.issues.set(result.issues);
            this.hasMore.set(result.hasMore);
        } catch (err) {
            if (request !== this.request) return;
            this.issues.set([]);
            this.hasMore.set(false);
            this.errorCode.set(githubErrorCode(err) ?? 'unknown');
            this.errorText.set(errorDetail(err, this.t()('github.loadFailed')));
            this.retryAtUtc.set(apiError(err)?.retryAtUtc ?? null);
        } finally {
            if (request === this.request) this.loading.set(false);
        }
    }
}
